// useInitialFocus.js: a dialog's initial focus (2026-10-14), the rule of the fullstack's #register
// (app/composables/useStepFocus.js; fullstack claude/FORMS.md § 17 Focus): when it's shown, the
// control that needs input, or else its main button. Applied when it's shown and again as its
// state loads, until the user acts: a click, a tap or a key press stops it, so their own changes
// never move the focus.
// isShown: () => boolean; getTarget: () => the element to focus, or null (not ready yet); tried
// for a few frames, while a dialog opens.
import {nextTick, onBeforeUnmount, onMounted, watch} from "vue";

const USER_ACTIONS = ["pointerdown", "keydown"];
// the ring shown though the dialog was opened with a click (browsers hide it when a script moves
// the focus after a mouse click; the initial focus should be seen), as #register's (2026-10-14)
const SHOWN = {focusVisible: true};
const MAX_FRAMES = 300; // about 5 s for the dialog to open and lay out (a slow load: 2026-10-14)

function getIsFocusable(target) {
  return Boolean(target) && !target.disabled && target.getClientRects().length > 0;
}

function useUserActionListener(onAction) {
  onMounted(() => USER_ACTIONS.forEach((name) => document.addEventListener(name, onAction, true)));
  onBeforeUnmount(() => {
    USER_ACTIONS.forEach((name) => document.removeEventListener(name, onAction, true));
  });
}

export function useInitialFocus(isShown, getTarget) {
  let isArmed = false;
  function tryFocus(framesLeft) {
    if (!isArmed || !isShown()) return;
    const target = getTarget();
    if (getIsFocusable(target)) return target.focus(SHOWN);
    if (framesLeft > 0) requestAnimationFrame(() => tryFocus(framesLeft - 1));
  }
  async function focusTarget() {
    await nextTick();
    requestAnimationFrame(() => tryFocus(MAX_FRAMES));
  }
  useUserActionListener(() => (isArmed = false));
  watch(isShown, (isNowShown) => {
    isArmed = isNowShown;
    if (isNowShown) focusTarget();
  }, {immediate: true, flush: "post"});
  watch(getTarget, focusTarget, {flush: "post"});
}
